import { Cta } from "@/components/Cta";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import { SectionHeader } from "@/components/SectionHeader";
import { TerminalCard } from "@/components/TerminalCard";
import { useSeo } from "@/lib/seo";
import { Card, CodeBlock, Container, Grid, SimpleGrid, Stack, Text, Title } from "@repo/ui/core";
import { IconBolt, IconBrandGitlab, IconStack2 } from "@repo/ui/icons";
import styles from "@styles/site.module.css";

const features = [
  {
    title: "Burst Compute",
    description:
      "High-compute nodes automatically provisioned when CI runs, shut down when builds finish. Pay only for what you use.",
    icon: IconBolt,
  },
  {
    title: "Intelligent Snapshots",
    description:
      "Filesystem snapshots captured and managed automatically. Snapshot and restore in seconds for filesystems hundreds of GB in size.",
    icon: IconStack2,
  },
  {
    title: "GitLab CI Templates",
    description:
      "Automated CI/CD templates for Android Automotive builds. Get started with a simple pipeline config.",
    icon: IconBrandGitlab,
  },
];

const steps = [
  {
    title: "Push code to GitLab",
    desc: "Standard git workflow. Configure projects for CI/CD in our console.",
  },
  {
    title: "Runners auto-provision",
    desc: "High-compute instances spin up instantly. No manual configuration needed.",
  },
  {
    title: "Snapshots accelerate builds",
    desc: "Previous build state restored in seconds. Incremental builds process only changes.",
  },
  {
    title: "Auto-deploy and test",
    desc: "Builds automatically deploy to emulators for validation. Full CI/CD in one pipeline.",
  },
];

export default function AndroidBuilds() {
  useSeo("/products/android-builds");

  return (
    <>
      <PageHero
        eyebrow="Android Builds"
        title={
          <>
            Faster Android Automotive <span className={styles.gradientText}>builds</span>
          </>
        }
        description="Full AAOSP builds in minutes on burst compute that only exists while your pipeline runs. Filesystem snapshots restore previous build state, so incremental builds process only what changed."
      >
        <Cta to="/contact">Book a Demo</Cta>
      </PageHero>

      {/* Features */}
      <Section>
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
          {features.map((feat, i) => (
            <Reveal key={feat.title} delay={i * 0.05}>
              <Card padding="xl" className={styles.card} style={{ height: "100%" }}>
                <Stack gap="md">
                  <span className={styles.iconChip}>
                    <feat.icon size={20} stroke={1.5} />
                  </span>
                  <Title order={3} size="1.125rem" fw={600}>
                    {feat.title}
                  </Title>
                  <Text c="dimmed" size="sm" lh={1.7}>
                    {feat.description}
                  </Text>
                </Stack>
              </Card>
            </Reveal>
          ))}
        </SimpleGrid>
      </Section>

      {/* How it works */}
      <Section hairline>
        <Grid gutter={64} align="center">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <SectionHeader title="From push to emulator in minutes" />
            <div>
              {steps.map((step, i) => (
                <Reveal key={step.title} delay={i * 0.05}>
                  <div className={styles.stepRow}>
                    <div>
                      <Text fw={600} mb={4}>
                        {step.title}
                      </Text>
                      <Text size="sm" c="dimmed" lh={1.6}>
                        {step.desc}
                      </Text>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Reveal delay={0.1} y={24}>
              <TerminalCard title="build-output.log">
                <CodeBlock
                  code={`$ nullctl build --target automotive_arm64
✓ Snapshot restored
✓ Dependencies cached
→ Building automotive_arm64...
  [============================] 100%
✓ Build complete (8m 42s)
→ Deploying to emulator...
✓ Emulator ready
  https://review-381-dk2f7a.emu.null.autos`}
                  language="bash"
                  withCopyButton={false}
                  style={{ fontSize: "0.8125rem", background: "transparent" }}
                  styles={{ pre: { background: "transparent", padding: 0, margin: 0, lineHeight: 1.8 } }}
                />
              </TerminalCard>
            </Reveal>
          </Grid.Col>
        </Grid>
      </Section>

      {/* CTA */}
      <section className={styles.finalCta}>
        <Container size="xl" py={88} ta="center">
          <Reveal>
            <Title order={2} fw={650} size="2rem" style={{ letterSpacing: "-0.02em" }}>
              Stop waiting for builds
            </Title>
            <Text size="lg" c="dimmed" maw={480} mx="auto" mt="md" mb="xl" lh={1.65}>
              See burst compute and snapshot restores running on your own codebase.
            </Text>
            <div className={styles.ctaRow} style={{ justifyContent: "center" }}>
              <Cta to="/contact">Book a Demo</Cta>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
